import React, { ComponentProps } from 'react'
import { TextInput, TextInputProps, TextStyle } from 'react-native'

import { isIOS } from '@zet/utils'
import { styled } from 'nativewind'

import { Stack } from '../layout/Stack'
import { colors } from '../tailwind'
import { B1, B2, B5 } from '../typography/index'

const inputFrameDefaultClass =
  'rounded border font-[Graphie-Book] text-sm leading-5 bg-neutral[white100] focus:font-[Graphie-SemiBold]'
const InputFrame = styled(TextInput, inputFrameDefaultClass, {
  variants: {
    type: {
      primary: 'border-grays-40 focus:border-primary-100',
      success: 'border-success-100 focus:border-primary-100',
      error: 'border-error-100 focus:border-primary-100',
      warning: 'border-warning-100 focus:border-primary-100',
      disabled: 'border-grays-20 bg-grays-5',
    },
    size: {
      sm: 'px-3 py-2',
      md: 'p-4/2',
      lg: 'h-[56] p-4',
    },
  },
  defaultProps: {
    size: 'md',
  },
})

const ErrorText = styled(B5, 'font-normal text-xs	leading-4 pt-2', {
  variants: {
    intent: {
      primary: 'text-grays-60',
      success: 'text-success-150',
      error: 'text-error-100',
      warning: 'text-warning-100',
      disabled: 'text-grays-60',
    },
  },
  defaultProps: {
    intent: 'primary',
  },
})

type InputFrameProps = ComponentProps<typeof InputFrame>
type ErrorTextProps = ComponentProps<typeof ErrorText>

type InputProps = InputFrameProps &
  ErrorTextProps &
  TextInputProps & {
    iconLeft?: React.ReactNode
    iconRight?: React.ReactNode
    showErrorText?: string
    isEditable?: boolean
    frameStyle?: TextStyle
    label?: string
    isRequired?: boolean
    noIconPadding?: boolean
    stylingOverrides?: {
      parentClassname?: string
    }
  }

export const Input = React.forwardRef<TextInput, InputProps>(
  (
    {
      type = 'primary',
      iconLeft,
      iconRight,
      size = 'md',
      showErrorText,
      isEditable = true,
      frameStyle = {},
      label,
      isRequired,
      noIconPadding,
      stylingOverrides = { parentClassname: '' },
      ...props
    },
    ref,
  ) => {
    return (
      <Stack className={stylingOverrides.parentClassname}>
        {label && (
          <B2 className="text-grays-80 mb-2">
            {label}
            {isRequired && <B1 className="text-error-100 pl-1">*</B1>}
          </B2>
        )}

        <Stack className="relative w-full">
          {iconLeft ? (
            <Stack
              className="absolute bottom-0 left-0 top-0 items-center justify-center px-2 z-10"
              style={{ height: '100%' }}
            >
              {iconLeft}
            </Stack>
          ) : null}
          <InputFrame
            editable={isEditable}
            ref={ref as React.RefObject<TextInput>}
            type={type}
            size={size}
            selectionColor={colors.grays[50]}
            placeholderTextColor={colors.grays[50]}
            {...props}
            style={[
              frameStyle,
              {
                paddingLeft: iconLeft ? 36 : 16,
                paddingRight: iconRight ? 36 : 16,
                paddingTop: isIOS ? 6 : undefined,
              },
              props.style,
            ]}
          />
          {iconRight ? (
            <Stack
              className="absolute bottom-0 right-0 top-0 items-center justify-center pr-3 z-10"
              style={{ height: '100%' }}
            >
              {iconRight}
            </Stack>
          ) : null}
        </Stack>

        {!!showErrorText && (
          <Stack className={`${noIconPadding ? 'mt-1' : 'mt-2'}`}>
            <ErrorText intent={type || undefined}>{showErrorText}</ErrorText>
          </Stack>
        )}
      </Stack>
    )
  },
)
